import React, { useEffect, useState } from 'react';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { FaPen } from 'react-icons/fa';
import { getSk, qa, updateQaScorecard, improveQuestion, deleteQaScorecard } from '../api/api';
import ConfirmationModal from '../components/ConfirmationModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

const ScorecardEditor = ({ setAlert }) => {
  const history = useHistory();
  const location = useLocation();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [scorecards, setScorecards] = useState([]);
  const [selectedScorecard, setSelectedScorecard] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [fields, setFields] = useState([]);
  const [initialFields, setInitialFields] = useState([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newScorecardName, setNewScorecardName] = useState('');
  const [loadingScorecards, setLoadingScorecards] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newVersionName, setNewVersionName] = useState('');
  const [confirmation, setConfirmation] = useState({ open: false, title: '', message: '' });
  const [deleteInfo, setDeleteInfo] = useState({ open: false });
  const [saveConfirm, setSaveConfirm] = useState(false);

  // Enable create mode when the "new" query parameter is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') {
      setCreatingNew(true);
    }
  }, [location.search]);

  // Auto-select scorecard when the "name" query parameter is provided
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const name = params.get('name');
    if (name) {
      setCreatingNew(false);
      setSelectedScorecard(name);
    }
  }, [location.search]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const result = await getSk('campaign');
        const itms = result.Items || [];
        const campaignOptions = itms
          .map(i => i.SK?.S)
          .filter(Boolean)
          .map(c => ({ label: c, value: c }));
        setCampaigns(campaignOptions);
      } catch (err) {
        console.error('Error loading campaigns:', err);
      }
    };
    fetchCampaigns();
  }, []);

useEffect(() => {
    const fetchScorecards = async () => {
      setLoadingScorecards(true);
      try {
        const res = await qa();
        const items = res.Items || res.items || [];
        const grouped = {};
        items.forEach(item => {
          const scorecardName = item.template?.S || item.scorecard?.S;
          if (!scorecardName) return;
          const version = item.version?.S || '1';
          const campaign = item.PK?.S || item.PK || item.campaign?.S || '';
          if (!grouped[scorecardName]) grouped[scorecardName] = { campaign, versions: {} };
          if (!grouped[scorecardName].versions[version]) grouped[scorecardName].versions[version] = [];
          grouped[scorecardName].versions[version].push({
            SK: item.SK?.S || '',
            description: item.description?.S || '',
            type: item.type?.S || 'boolean',
            weight: parseInt(item.weight?.S || 0)
          });
        });
        const arr = Object.keys(grouped).map(name => ({ name, campaign: grouped[name].campaign, versions: grouped[name].versions }));
        setScorecards(arr);
      } catch (err) {
        console.error('Error loading scorecards:', err);
      } finally {
        setLoadingScorecards(false);
      }
    };
    fetchScorecards();
  }, []);

  useEffect(() => {
    if (!selectedScorecard || creatingNew) {
      setFields([]);
      setInitialFields([]);
      setDirty(false);
      setNewVersionName('');
      return;
    }
    const tpl = scorecards.find(t => t.name === selectedScorecard);
    if (!tpl) {
      setFields([]);
      setInitialFields([]);
      setDirty(false);
      return;
    }
    setSelectedCampaign(tpl.campaign || '');
    const versions = Object.keys(tpl.versions).sort((a, b) => parseInt(a) - parseInt(b));
    let versionKey = selectedVersion;
    if (!versionKey) {
      versionKey = versions[versions.length - 1];
      setSelectedVersion(versionKey);
    }
    const loaded = tpl.versions[versionKey] || [];
    setFields(loaded);
    setInitialFields(loaded);
    setDirty(false);
    setNewVersionName('');
  }, [selectedScorecard, selectedVersion, scorecards, creatingNew]);

  const handleFieldChange = (index, key, value) => {
    setFields(prev => prev.map((f, i) => (i === index ? { ...f, [key]: value } : f)));
    setDirty(true);
  };

  const addField = () => {
    setFields(prev => [
      ...prev,
      { SK: '', description: '', type: 'boolean', weight: 10 }
    ]);
    setDirty(true);
  };

  const removeField = (index) => {
    setFields(prev => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const handleDeleteVersion = async () => {
    const tpl = scorecards.find(t => t.name === selectedScorecard);
    if (!tpl) return;
    const versionFields = tpl.versions[selectedVersion] || [];
    try {
      await Promise.all(
        versionFields.map(f => deleteQaScorecard(selectedCampaign, f.SK))
      );
      setConfirmation({
        open: true,
        title: 'Version Deleted',
        message: `Version ${selectedVersion} deleted.`
      });
      setSelectedVersion('');
      setDirty(false);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Delete failed:', err);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: 'Failed to delete version.'
      });
    } finally {
      setDeleteInfo({ open: false });
    }
  };

  const handleImproveQuestion = async (index) => {
    const text = fields[index].description;
    if (!text.trim()) return;
    try {
      const result = await improveQuestion(text);
      if (result && result.question) {
        setFields(prev =>
          prev.map((f, i) => (i === index ? { ...f, description: result.question } : f))
        );
      }
    } catch (err) {
      console.error('AI assist failed:', err);
    }
  };

  const handleCampaignChange = (value) => {
    if (dirty && value !== selectedCampaign) {
      const proceed = window.confirm('Discard unsaved changes?');
      if (!proceed) return;
    }
    setSelectedCampaign(value);
  };

  const handleScorecardChange = (value) => {
    if (dirty && value !== selectedScorecard && value !== '__new__') {
      const proceed = window.confirm('Discard unsaved changes?');
      if (!proceed) return;
    }
    if (value === '__new__') {
      setCreatingNew(true);
      setSelectedScorecard('');
      setSelectedVersion('');
      setNewScorecardName('');
      setFields([]);
      setInitialFields([]);
      setSelectedCampaign('');
    } else {
      setCreatingNew(false);
      setSelectedScorecard(value);
      setSelectedVersion('');
      const sc = scorecards.find(t => t.name === value);
      if (sc) setSelectedCampaign(sc.campaign || '');
    }
  };

  const handleVersionChange = (value) => {
    if (dirty && value !== selectedVersion) {
      const proceed = window.confirm('Discard unsaved changes?');
      if (!proceed) return;
    }
    setSelectedVersion(value);
  };

  const handleSave = async () => {
    if (fields.some(f => !f.SK.trim())) {
      setAlert({
        variant: 'warning',
        heading: 'Validation Error',
        text: 'All fields must have a name.'
      });
      setSaveConfirm(false);
      return;
    }
    if (dirty && !newVersionName.trim()) {
      setAlert({
        variant: 'warning',
        heading: 'Validation Error',
        text: 'Please provide a version name before saving.'
      });
      setSaveConfirm(false);
      return;
    }
    setSaving(true);

    const noChanges = !dirty;

    if (creatingNew && !newScorecardName.trim()) {
      setAlert({
        variant: 'warning',
        heading: 'Validation Error',
        text: 'Scorecard name is required.'
      });
      setSaving(false);
      return;
    }


    const templateName = creatingNew ? newScorecardName : selectedScorecard;
    const newVersion = dirty ? newVersionName : selectedVersion || '1';

    const payload = fields.map(f => ({
      PK: selectedCampaign,
      SK: f.SK,
      template: templateName,
      version: String(newVersion),
      description: f.description,
      type: f.type,
      weight: f.weight,
      updater: true
    }));

    // determine removed fields from current version when editing
    const removed = !creatingNew ? initialFields.filter(
      of => !fields.some(f => f.SK === of.SK)
    ) : [];

    try {
      if (removed.length > 0) {
        await Promise.all(
          removed.map(f => deleteQaScorecard(selectedCampaign, f.SK))
        );
      }
      await updateQaScorecard(payload);
      setAlert({
        variant: 'success',
        heading: noChanges ? 'No Changes Detected' : 'Scorecard Updated',
        text: noChanges
          ? 'No changes detected. Saved anyway for confirmation.'
          : 'Scorecard updated successfully.'
      });
      setConfirmation({
        open: true,
        title: 'Scorecard Saved',
        message: `Version ${newVersion} saved successfully.`
      });
      setDirty(false);
      setNewVersionName('');
      setTimeout(() => history.push('/scorecards'), 1500);
    } catch (err) {
      console.error('Update failed:', err);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: 'Failed to update scorecard.'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Prompt
        when={dirty}
        message="You have unsaved changes. Are you sure you want to leave?"
      />
      <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Scorecards</h1>
          <p className="text-gray-600 mt-1">Create or edit QA scorecards</p>
        </div>
      </div>
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Scorecard</label>
            {loadingScorecards ? (
              <div className="text-gray-500">Loading scorecards...</div>
            ) : (
              <select
                className="select-field"
                value={creatingNew ? '__new__' : selectedScorecard}
                onChange={e => handleScorecardChange(e.target.value)}
              >
                <option value="">Select scorecard...</option>
                {scorecards.map(t => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
                <option value="__new__">Create new scorecard...</option>
              </select>
            )}
          </div>
          {creatingNew ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Campaign (optional)</label>
              <select
                className="select-field"
                value={selectedCampaign}
                onChange={e => handleCampaignChange(e.target.value)}
              >
                <option value="">Select campaign...</option>
                {campaigns.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          ) : selectedScorecard && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Campaign</label>
              <input type="text" className="input-field" value={selectedCampaign} disabled />
            </div>
          )}
          {!creatingNew && selectedScorecard && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
              <select
                className="select-field"
                value={selectedVersion}
                onChange={e => handleVersionChange(e.target.value)}
              >
                {Object.keys(scorecards.find(s => s.name === selectedScorecard)?.versions || {})
                  .sort((a,b) => parseInt(a) - parseInt(b))
                  .map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
              </select>
            </div>
          )}
        </div>
      </div>
      {creatingNew && (
        <div className="card">
          <label className="block text-sm font-medium text-gray-700 mb-2">Scorecard Name</label>
          <input
            type="text"
            className="input-field"
            value={newScorecardName}
            onChange={e => { setNewScorecardName(e.target.value); setDirty(true); }}
          />
        </div>
      )}
      {(selectedScorecard || creatingNew) && (
        <div className="card space-y-6">
          {fields.map((field, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    className="input-field"
                    value={field.SK}
                    onChange={e => handleFieldChange(idx, 'SK', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    className="select-field"
                    value={field.type}
                    onChange={e => handleFieldChange(idx, 'type', e.target.value)}
                  >
                    <option value="boolean">Yes/No</option>
                    <option value="score">Score (0-100)</option>
                    <option value="rating">Rating (1-5)</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                  <div className="relative">
                    <textarea
                      rows="2"
                      className="input-field pr-10"
                      value={field.description}
                      onChange={e => handleFieldChange(idx, 'description', e.target.value)}
                    />
                    <FaPen
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
                      onClick={() => handleImproveQuestion(idx)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight (%)</label>
                  <input
                    type="number"
                    className="input-field"
                    value={field.weight}
                    onChange={e => handleFieldChange(idx, 'weight', e.target.value)}
                  />
                </div>
              </div>
              <button
                onClick={() => removeField(idx)}
                className="btn-outline text-red-600 border-red-300 hover:bg-red-50 mt-2"
              >
                Remove Field
              </button>
            </div>
          ))}
          <button onClick={addField} className="btn-outline">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Field
          </button>
          {dirty && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Version Name</label>
              <input
                type="text"
                className="input-field"
                value={newVersionName}
                onChange={e => setNewVersionName(e.target.value)}
              />
            </div>
          )}
          <div className="flex justify-end space-x-2">
            {!creatingNew && selectedScorecard && selectedVersion && (
              <button
                onClick={() => setDeleteInfo({ open: true })}
                className="btn-outline text-red-600 border-red-300 hover:bg-red-50"
              >
                Delete Version
              </button>
            )}
            <button
              onClick={() => setSaveConfirm(true)}
              disabled={saving}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
    <ConfirmationModal
      open={confirmation.open}
      title={confirmation.title}
      message={confirmation.message}
      onClose={() => {
        setConfirmation({ ...confirmation, open: false });
      }}
    />
    <ConfirmationModal
      open={saveConfirm}
      title="Save Confirmation"
      message={`Are you sure you want to save the changes to ${creatingNew ? newScorecardName || 'this scorecard' : selectedScorecard}?`}
      confirmText="Confirm Save"
      onClose={() => setSaveConfirm(false)}
      onConfirm={handleSave}
    />
    <DeleteConfirmationModal
      open={deleteInfo.open}
      itemName={`Version ${selectedVersion}`}
      onClose={() => setDeleteInfo({ open: false })}
      onConfirm={handleDeleteVersion}
    />
    </>
  );
};

export default ScorecardEditor;
